// =============================================================================
// AGC: DROID FARM - AI Provider Selector
// AGC: UI for selecting AI provider (Claude/OpenAI) for lot detection
// =============================================================================

import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import type { AIProvider, ProviderStatus } from '../../types/lot-detection';

interface AIProviderSelectorProps {
  selectedProvider: AIProvider;
  onProviderChange: (provider: AIProvider) => void;
  status?: ProviderStatus | null;
  compact?: boolean;
}

// AGC: Provider configuration - includes Google Vision
const providers: Record<AIProvider, { name: string; shortName: string; color: string; bgColor: string; icon: string }> = {
  anthropic: {
    name: 'Claude Vision',
    shortName: 'Claude',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    icon: '🧠',
  },
  openai: {
    name: 'GPT-4 Vision',
    shortName: 'GPT-4',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    icon: '🤖',
  },
  googleVision: {
    name: 'Google Vision',
    shortName: 'Google',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    icon: '👁️',
  },
};

/**
 * AGC: AI Provider selector component
 * AGC: Allows switching between Claude and GPT-4 for detection
 */
export const AIProviderSelector: React.FC<AIProviderSelectorProps> = ({
  selectedProvider,
  onProviderChange,
  status,
  compact = false,
}) => {
  const { t } = useTranslation();

  // AGC: Compact toggle variant
  if (compact) {
    return (
      <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
        {(Object.keys(providers) as AIProvider[]).map((id) => {
          const provider = providers[id];
          const isSelected = selectedProvider === id;
          const isAvailable = status ? (status[id]?.available ?? true) : true;

          return (
            <button
              key={id}
              onClick={() => onProviderChange(id)}
              disabled={!isAvailable}
              className={`
                px-2 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1
                ${isSelected ? 'bg-white dark:bg-gray-600 shadow text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}
                ${!isAvailable ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={provider.name}
            >
              <span>{provider.icon}</span>
              <span className="hidden sm:inline">{provider.shortName}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // AGC: Full card variant
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('lotDetection.selectProvider')}
      </label>
      <div className="grid grid-cols-3 gap-2">
        {(Object.keys(providers) as AIProvider[]).map((id) => {
          const provider = providers[id];
          const isSelected = selectedProvider === id;
          const isAvailable = status ? (status[id]?.available ?? true) : true;

          return (
            <button
              key={id}
              onClick={() => onProviderChange(id)}
              disabled={!isAvailable}
              className={`
                p-3 rounded-xl border-2 text-center transition-all
                ${isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'}
                ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* AGC: Provider icon */}
              <div className={`w-10 h-10 mx-auto rounded-lg ${provider.bgColor} ${provider.color} flex items-center justify-center mb-2`}>
                <span className="text-xl">{provider.icon}</span>
              </div>
              
              {/* AGC: Provider name */}
              <p className="font-semibold text-gray-900 dark:text-white text-sm">{provider.shortName}</p>
              
              {/* AGC: Model info */}
              {status && status[id]?.model && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                  {status[id].model}
                </p>
              )}
              
              {/* AGC: Availability indicator */}
              {!isAvailable && (
                <p className="text-xs text-red-500 mt-1">
                  {t('lotDetection.providerUnavailable')}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// AGC: Provider Badge
// =============================================================================

interface AIProviderBadgeProps {
  provider: AIProvider;
  className?: string;
}

/**
 * AGC: Small badge showing which provider was used
 */
export const AIProviderBadge: React.FC<AIProviderBadgeProps> = ({ provider, className = '' }) => {
  const info = providers[provider] || providers.anthropic; // AGC: Fallback if unknown provider
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${info.bgColor} ${info.color} ${className}`}>
      <span>{info.icon}</span>
      {info.shortName}
    </span>
  );
};

// =============================================================================
// AGC: Provider Status Display
// =============================================================================

interface AIProviderStatusProps {
  status: ProviderStatus | null;
}

/**
 * AGC: Display status of all providers
 */
export const AIProviderStatus: React.FC<AIProviderStatusProps> = ({ status }) => {
  const { t } = useTranslation();

  if (!status) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t('lotDetection.checkingProviders')}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm flex-wrap">
      {(Object.keys(providers) as AIProvider[]).map((id) => {
        const provider = providers[id];
        const isAvailable = status[id]?.available ?? false;

        return (
          <div key={id} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${isAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-xs">{provider.icon}</span>
            <span className={`text-xs ${isAvailable ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              {provider.shortName}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// AGC: Fallback Configuration Toggle
// =============================================================================

interface AIFallbackConfigProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  fallbackProvider: AIProvider;
}

/**
 * AGC: Toggle for enabling/disabling automatic fallback
 */
export const AIFallbackConfig: React.FC<AIFallbackConfigProps> = ({
  enabled,
  onToggle,
  fallbackProvider,
}) => {
  const { t } = useTranslation();
  const provider = providers[fallbackProvider];

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('lotDetection.autoFallback')}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('lotDetection.fallbackTo', { provider: provider.name })}
        </p>
      </div>
      <button
        onClick={() => onToggle(!enabled)}
        className={`
          relative w-11 h-6 rounded-full transition-colors
          ${enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}
        `}
      >
        <span
          className={`
            absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
            ${enabled ? 'translate-x-5' : 'translate-x-0.5'}
          `}
        />
      </button>
    </div>
  );
};

export default AIProviderSelector;

